// The DEN set-top box's channel list, and the user's favourites among it.
//
// Used by the DEN pad to offer "jump straight to Star Plus" instead of making
// the user key 1-0-1. The jump itself is an ordinary IR token —
// `DNCHOICE<n>`, see core/devices/irRemote — so this module only ever READS.
//
// ── Two live endpoints, one of which lies ──────────────────────────────────
//
//   GetTelevisonRemote      {}            216 rows
//     { TvRemoteId, SetupBoxName, ChannelNumber, ChannelName, Favourite }
//
//   GetUserRemotefavourite  {UserId}      41 rows
//     { TvRemoteId, UserId, ChannelNumber }
//
// ⚠️ `GetUserRemotefavourite` IGNORES ITS UserId AND RETURNS EVERY USER'S ROWS.
// Probed 2026-08-08: asking for UserId 1 came back with rows for UserId 35 and
// 46 as well. A straight port of the Flutter call therefore shows one customer
// another customer's channel list — and it looks perfectly plausible, because it
// is a list of real channels.
//
// So the filter below is NOT redundant defensive coding, and it must not be
// removed as such. It is the only thing making the reply mean what its request
// asked for. `Favourite` on the catalog row is likewise a global flag, not this
// user's — it is ignored here for the same reason.
//
// ── Read-only, deliberately ────────────────────────────────────────────────
// Flutter can add and remove favourites, through the generic `/insert` and
// `/delete` table routes with a client-built WHERE clause
// (den_remote.dart:56-60). Those are not called from here: nobody has probed
// what they do on this deployment, and a generic table-delete driven by a string
// the client assembles deserves a look before it ships. Reading is useful on its
// own — there are already favourites saved for these accounts.

import { post } from './api';

const rows = (res) => (Array.isArray(res && res.data) ? res.data : []);

/** Every channel the DEN box knows. */
export const getTvChannels = () => post('GetTelevisonRemote', {}).then(rows).catch(() => []);

/**
 * This user's favourite channels, with names attached.
 *
 * -> [{ channelNumber, channelName }], ascending by channel number.
 */
export async function getFavouriteChannels(userId) {
  const [favs, catalog] = await Promise.all([
    post('GetUserRemotefavourite', { UserId: userId }).then(rows).catch(() => []),
    getTvChannels(),
  ]);

  // ⚠️ THE LINE THE ENDPOINT MAKES NECESSARY — see the note at the top.
  const mine = favs.filter((f) => Number(f.UserId) === Number(userId));

  const nameOf = new Map(
    catalog.map((c) => [Number(c.ChannelNumber), String(c.ChannelName || '').trim()]),
  );

  const seen = new Set();
  return mine
    .map((f) => Number(f.ChannelNumber))
    .filter((n) => Number.isFinite(n) && !seen.has(n) && seen.add(n))
    .sort((a, b) => a - b)
    .map((n) => ({ channelNumber: n, channelName: nameOf.get(n) || `Channel ${n}` }));
}

export default getFavouriteChannels;
